import type { ComponentPropsWithoutRef, FC, MouseEventHandler, ReactNode } from "react"
import { useHotkeys } from "react-hotkeys-hook"

import { cn } from "../lib/utils"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../shadcnBase/ShadcnAlertDialog"
import { Loader } from "./Loader"

export type AixAlertDialogProp = {
  title: string | ReactNode
  description?: ReactNode
  cancelLabel?: string
  closePopup?: ReactNode
  className?: string
  size?: string
  actionLabel?: string
  actionClassName?: string
  onActionClick?: MouseEventHandler<HTMLButtonElement>
  icon?: ReactNode
  loading?: boolean
} & ComponentPropsWithoutRef<typeof AlertDialog>

export const AixAlertDialog: FC<AixAlertDialogProp> = ({
  title,
  description,
  cancelLabel = "Cancel",
  actionLabel = "",
  actionClassName,
  onActionClick,
  icon,
  loading,
  className,
  size = "lg",
  ...p
}) => {
  useHotkeys("enter", (e) => {
    if (p?.open) {
      onActionClick?.(e as any)
    }
  })

  return (
    <AlertDialog {...p}>
      <AlertDialogContent className={cn(className, `max-w-${size}`)}>
        <AlertDialogHeader className="flex flex-row items-start pb-4">
          <div className="flex-shrink-0 pt-2">{icon}</div>
          <div className={cn({ "pl-3": !!icon })}>
            <AlertDialogTitle className="pb-1 text-start font-heading text-xl">{title}</AlertDialogTitle>
            <AlertDialogDescription className="text-start">{description}</AlertDialogDescription>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>{cancelLabel}</AlertDialogCancel>

          {actionLabel && (
            <AlertDialogAction className={actionClassName} onClick={onActionClick} disabled={loading}>
              {loading && <Loader className="mr-1" />}
              <span>{actionLabel}</span>
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export const AixAlertDialogV2: FC<AixAlertDialogProp> = ({
  title,
  description,
  icon,
  closePopup,
  className,
  size = "lg",
  ...p
}) => {
  return (
    <AlertDialog {...p}>
      <AlertDialogContent className={cn(className, `max-w-${size}`)}>
        <AlertDialogHeader className="relative flex flex-col">
          <div className="flex w-full items-start justify-between">
            <div className={cn({ "pl-3": !!icon })}>
              <AlertDialogTitle className="pb-1 text-start font-heading font-medium text-gray-500">
                {title}
              </AlertDialogTitle>
              <AlertDialogDescription className="mt-1 text-start">{description}</AlertDialogDescription>
            </div>
            <div className="absolute -right-3 -top-[2px] cursor-pointer">{closePopup}</div>
          </div>
        </AlertDialogHeader>
      </AlertDialogContent>
    </AlertDialog>
  )
}
